import React from 'react';
import { Form, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import _isEqual from 'lodash/isEqual';

// types
import type { ThemeBucketView } from '@zola/svc-web-api-ts-client';
import type { OptionType } from '@zola/zola-ui/src/components/Form/DropdownCheckboxList/DropdownCheckboxList';

// redux
import { useAppSelector } from 'reducers/useAppSelector';

// helpers + utils
import { PLPFilterOptions } from 'pages/WebsiteDesignsPLPSSR/helpers';
import { SEGMENTS, segments } from 'pages/WebsiteDesignsPLPV2/utils';

// components
import { SegmentControlV2 } from '@zola/zola-ui/src/components/SegmentControlV2';
import DropdownCheckboxList from '@zola/zola-ui/src/components/Form/DropdownCheckboxList';

// styles
import {
  ColorIndicator,
  ColorOptions,
} from 'pages/WebsiteDesignsPLPSSR/ThemesFilter/ThemesFilter.styles';
import { hideWhenTabletAndMobile } from 'pages/WebsiteDesignsPLPV2/WebsiteDesignsPLPV2.styles';
import {
  PLPControlsBarContainer,
  ResultsTxt,
  ChipContainer,
  ChipWrapper,
  ResultAndSort,
  StyledDropdown,
} from './PLPControlsBar.styles';

export type PlpControlsBarProps = {
  onSort: (sort: string | number | null) => void;
  onToggleLayout: (key: string | SEGMENTS) => void;
  onUpdateFilter: (filters: PLPFilterOptions) => void;
  setLayoutType: (key: string | SEGMENTS) => void;
};

const PLPControlsBar: React.FC<PlpControlsBarProps> = ({
  onSort,
  onToggleLayout,
  onUpdateFilter,
  setLayoutType,
}) => {
  const currentFilters = useAppSelector(state => state.websitePlp.applied_filters);
  const currentSort = useAppSelector(state => state.websitePlp.applied_sort);
  const resultsCount = useAppSelector(state => state.websitePlp.displayable_total);
  const sortOptions = useAppSelector(state => state.websitePlp.sorts);
  const facets = useAppSelector(state => state.websitePlp.facets);

  const filteredFacets = facets.filter(facet => facet.field !== 'layout_types');

  const getSortTxt = () => {
    switch (`${currentSort}`) {
      case 'POPULARITY':
        return 'Sort: Popularity';
      case 'RECENTLY_ADDED':
        return 'Sort: Recently Added';
      default:
        return 'Sort: Featured';
    }
  };

  const handleToggleLayoutType = (key: string | SEGMENTS) => {
    setLayoutType(key);
    onToggleLayout(key);
  };

  const renderColorLabel = (colorOption: ThemeBucketView): JSX.Element => {
    return (
      <ColorOptions>
        <ColorIndicator className={colorOption?.value?.toLowerCase()} />
        {colorOption?.name}
      </ColorOptions>
    );
  };

  const renderChips = (): JSX.Element[] => {
    return filteredFacets.map(({ name: label, buckets, field }) => {
      // Create the dropdown options
      let options: {
        label: string | JSX.Element | undefined;
        value: string | undefined;
      }[];
      if (buckets && buckets.length > 0) {
        options = buckets.map(option => {
          let labelVal: JSX.Element | string = '';
          if (field === 'colors') {
            labelVal = renderColorLabel(option);
          } else if (option.name) {
            labelVal = option.name;
          }
          return {
            label: labelVal,
            value: option.value,
          };
        });
      }
      // Return the chip and list to be used by the form
      return (
        <ChipWrapper key={field} field={field}>
          {field && label && (
            <FieldArray name={field}>
              {({ ...props }) => (
                <DropdownCheckboxList
                  id={label}
                  label={label}
                  options={options as OptionType[]}
                  includeCountInLabel
                  withCaret
                  {...props}
                />
              )}
            </FieldArray>
          )}
        </ChipWrapper>
      );
    });
  };

  const facetsMap: Record<string, Record<string, string>> = {};
  filteredFacets.forEach(facetGroup => {
    if (facetGroup && facetGroup.field) {
      const { field } = facetGroup;
      facetsMap[field] = {};

      if (facetGroup.buckets && facetGroup.buckets.length > 0) {
        facetGroup.buckets.forEach(facetOption => {
          const { value, name } = facetOption;
          if (value && name) {
            facetsMap[field][value] = name;
          }
        });
      }
    }
  });

  return (
    <PLPControlsBarContainer css={hideWhenTabletAndMobile} data-testid="PLPControlsBar">
      <div>
        <Form
          onSubmit={v => onUpdateFilter(v)}
          mutators={{ ...arrayMutators }}
          initialValues={currentFilters}
          render={formProps => {
            const { handleSubmit, form } = formProps;
            return (
              <form
                onSubmit={e => {
                  handleSubmit(e)?.catch(() => undefined);
                }}
              >
                <ChipContainer>{renderChips()}</ChipContainer>
                <FormSpy
                  subscription={{ values: true }}
                  onChange={({ values }): void => {
                    if (!_isEqual(values, currentFilters)) form.submit()?.catch(() => undefined);
                  }}
                />
              </form>
            );
          }}
        />
      </div>
      <div>
        <SegmentControlV2 segments={segments} onClick={handleToggleLayoutType} light />
      </div>
      <div>
        <ResultAndSort>
          {resultsCount && <ResultsTxt>{resultsCount} results</ResultsTxt>}
          <StyledDropdown
            nullable={false}
            onChange={() => undefined}
            onSelect={onSort}
            options={
              sortOptions.map(opt => ({
                label: opt.description,
                value: opt.key,
              })) as any[]
            }
            placeholder={getSortTxt()}
            value={getSortTxt()}
            data-testid="PLPControlsBar__Sort"
          />
        </ResultAndSort>
      </div>
    </PLPControlsBarContainer>
  );
};

export default PLPControlsBar;
